<template>
  <v-dialog v-model="editApplicationDialog" scrollable max-width="800px">
    <v-card v-if="item">
      <v-card-title class="primary white--text">
        <h3 class="headline">{{item.job}} - {{item.employee_first_name}} {{item.employee_last_name}}</h3>
        <v-spacer/>
        <v-btn text small fab @click.native="editApplicationDialog = false">
          <v-icon color="white">close</v-icon>
        </v-btn>
      </v-card-title>
      <v-card-text>
        <v-container>
          <v-row>
            <v-col :cols="12">
              <strong>{{ $t('texts.applicationStatus.change') }}</strong>
              <v-radio-group
                v-model="form.status"
                hide-details
              >
                <v-radio
                  :label="$t('texts.applicationStatus.CONTRACTSIGNED')"
                  value="CONTRACTSIGNED"
                  color="primary"
                />
                <v-radio
                  :label="$t('texts.applicationStatus.REFUSAL')"
                  value="REFUSAL"
                  color="primary"
                />
                <v-radio
                  :label="$t('texts.applicationStatus.REFUSALEMPLOYEE')"
                  value="REFUSALEMPLOYEE"
                  color="primary"
                />
              </v-radio-group>
            </v-col>
          </v-row>
          <v-row>
            <v-col :cols="12">
              <v-card
                v-if="form.status === 'CONTRACTSIGNED'"
                flat color="blue lighten-5"
                class="mt-3"
              >
                <v-card-text>
                  <v-container grid-list-xl class="pa-0">
                    <form>
                      <v-row>
                        <v-col :cols="12">
                          <v-autocomplete
                            :items="jobItems"
                            v-model="form.occupied_job_id"
                            :label="$t('texts.job')"
                            item-value="id"
                            item-text="title"
                            required
                            data-vv-name="job_id"
                            :data-vv-as="$t('texts.job')"
                            v-validate="''"
                            :error-messages="errors.collect('job_id')"
                            @input="changeJobTitle($event)"
                          />
                        </v-col>
                        <v-col :cols="12" v-if="!form.occupied_job_id">
                          <v-radio-group
                            v-model="form.registration_requirement_status"
                            hide-details
                            required
                            data-vv-name="registration_requirement_status"
                            v-validate="'required'"
                            :error-messages="errors.collect('registration_requirement_status')"
                            class="mt-0"
                          >
                            <v-radio
                              :label="$t('texts.noSubjectToRegistration')"
                              value="NO_REGISTRATION_REQUIREMENT"
                              color="primary"
                            />
                            <v-radio
                              :label="$t('texts.jobNotifiedApproved')"
                              value="JOBROOM_EXTERNAL_APPROVED"
                              color="primary"
                            />
                          </v-radio-group>
                        </v-col>
                        <v-col :cols="12">
                          <v-text-field
                            v-model="form.job_title"
                            :label="$t('texts.insertJobtitle')"
                            required
                            :error-messages="errors.collect('insertJobtitle')"
                            v-validate="'required'"
                            data-vv-name="insertJobtitle"
                            :data-vv-as="$t('texts.insertJobtitle')"
                          />
                        </v-col>
                        <v-col :cols="12">
                          <v-text-field
                            v-model="form.workload"
                            :label="$t('texts.percent')"
                            suffix="%"
                            required
                            :error-messages="errors.collect('percent')"
                            v-validate="'required|numeric|min_value:1|max_value:100'"
                            data-vv-name="percent"
                            :data-vv-as="$t('texts.percent')"
                          />
                        </v-col>
                        <v-col :cols="12">
                          <v-menu
                            ref="startDate"
                            :close-on-content-click="false"
                            v-model="startDatePicker"
                            transition="scale-transition"
                            offset-y
                            min-width="290px"
                            :return-value.sync="form.start_date"
                          >
                            <template v-slot:activator="{ on }">
                              <v-text-field
                                v-on="on"
                                :label="$t('texts.startOfWork')"
                                :value="form.start_date | formatDateMedium"
                                v-validate="'required'"
                                readonly
                                required
                                :error-messages="errors.collect('start_date')"
                                data-vv-name="start_date"
                                :data-vv-as="$t('texts.startOfWork')"
                              />
                            </template>
                            <v-date-picker
                              v-model="form.start_date"
                              @input="$refs.startDate.save(form.start_date)"
                              header-color="primary"
                              color="primary"
                              locale="de-ch"
                              :first-day-of-week="1"
                              ref="startDatePicker"
                              :min="startDatePickerMin"
                              :max="startDatePickerMax"
                            />
                          </v-menu>
                          <input
                            hidden
                            v-model="form.start_date"
                            ref="startDateInput"
                            v-validate="'required|date_format:yyyy-MM-dd'"
                            data-vv-name="start_date"
                            :data-vv-as="$t('texts.startOfWork')"
                          />
                        </v-col>
                        <v-col :cols="12">
                          <v-menu
                            ref="endDate"
                            :close-on-content-click="false"
                            v-model="endDatePicker"
                            transition="scale-transition"
                            offset-y
                            min-width="290px"
                            :return-value.sync="form.end_date"
                          >
                            <template v-slot:activator="{ on }">
                              <v-text-field
                                v-on="on"
                                :label="$t('texts.endOfWork')"
                                :value="form.end_date | formatDateMedium"
                                readonly
                                required
                                v-validate="'required'"
                                data-vv-name="end_date"
                                :data-vv-as="$t('texts.endOfWork')"
                                :error-messages="errors.collect('end_date')"
                              />
                            </template>
                            <v-date-picker
                              v-model="form.end_date"
                              @input="$refs.endDate.save(form.end_date)"
                              header-color="primary"
                              color="primary"
                              locale="de-ch"
                              :first-day-of-week="1"
                              ref="endDatePicker"
                              :min="endDatePickerMin"
                              :max="endDatePickerMax"
                            />
                          </v-menu>
                          <input
                            hidden
                            v-model="form.end_date"
                            v-validate="'required|date_format:yyyy-MM-dd|after:startDateInput|season:startDateInput'"
                            data-vv-name="end_date"
                            :data-vv-as="$t('texts.endOfWork')"
                          />
                        </v-col>
                      </v-row>
                    </form>
                  </v-container>
                </v-card-text>
              </v-card>
              <v-card
                flat
                v-if="form.status === 'REFUSAL'"
                color="blue lighten-5"
                class="mt-3"
              >
                <v-card-text>
                  <v-container grid-list-xl class="pa-0">
                    <form>
                      <v-row>
                        <v-col :cols="12">
                          <v-textarea
                            v-model="form.explanation"
                            :label="$t('texts.callOff')"
                            required
                            :error-messages="errors.collect('callOff')"
                            v-validate="'required'"
                            data-vv-name="callOff"
                            :data-vv-as="$t('texts.callOff')"
                          />
                        </v-col>
                      </v-row>
                    </form>
                  </v-container>
                </v-card-text>
              </v-card>
            </v-col>
          </v-row>
        </v-container>
      </v-card-text>
        <v-divider/>
        <v-card-actions class="pr-3">
          <v-spacer/>
          <v-btn
            color="primary"
            class="white--text"
            :loading="loadingButton"
            :disabled="loadingButton"
            @click="update(item)"
          >
            {{ $t('texts.updateApplication') }}
            <v-icon right class="white--text">check_circle</v-icon>
          </v-btn>
        </v-card-actions>
    </v-card>
  </v-dialog>
</template>

<script>
import moment from 'moment';
import { createNamespacedHelpers } from 'vuex';

const { mapActions: mapCompanyApplicationsActions } = createNamespacedHelpers('company/applications');
const { mapState: mapCompanyJobsState } = createNamespacedHelpers('company/jobs');

export default {
  name: 'EditApplicationDialog',
  data() {
    return {
      editApplicationDialog: false,
      loadingButton: false,
      item: {},
      startDatePicker: false,
      endDatePicker: false,
      form: {
        status: '',
        occupied_job_id: null,
        job_title: '',
        workload: null,
        start_date: null,
        end_date: null,
        explanation: '',
        registration_requirement_status: null,
      },
    };
  },
  $_veeValidate: {
    validator: 'new',
  },
  mounted() {
    this.$eventBus.$on('show-edit-application-dialog', (item) => {
      this.item = item;
      const startDate = moment(`${item.start_year}-${item.start_month}-01`).format('YYYY-MM-DD');
      const endDate = moment(`${item.end_year}-${item.end_month}-01`).format('YYYY-MM-DD');
      this.form.status = item.status;
      this.form.job_id = item.job_id;
      const jobExists = this.jobs.active.find((job) => job.id === item.job_id);
      this.form.occupied_job_id = jobExists ? jobExists.id : null;
      this.form.workload = jobExists ? jobExists.workload_min : null;
      this.form.job_title = item.job;
      this.form.start_date = startDate;
      this.form.end_date = endDate;
      this.editApplicationDialog = true;
    });
  },
  computed: {
    ...mapCompanyJobsState([
      'jobs',
    ]),
    selectedCompanyId() {
      return this.$route.params.companyId;
    },
    startDatePickerMin() {
      return moment().subtract(9, 'months').format('YYYY-MM');
    },
    startDatePickerMax() {
      return moment().add(36, 'months').format('YYYY-MM');
    },
    endDatePickerMin() {
      if (this.form.start_date !== null) {
        return this.form.start_date;
      }
      return '';
    },
    endDatePickerMax() {
      return moment(this.form.start_date).add(10, 'months').format('YYYY-MM-DD');
    },
    jobItems() {
      const noJob = {
        id: null,
        title: this.$i18n.t('texts.withoutJobAdvertisement'),
      };
      return [...this.jobs.active, noJob];
    },
  },
  methods: {
    ...mapCompanyApplicationsActions([
      'updateApplication',
    ]),
    setWithoutJob() {
      this.form.occupied_job_id = null;
    },
    changeJobTitle(payload) {
      const selectedJob = this.jobs.active.find((item) => item.id === payload);
      if (selectedJob) {
        this.form.job_title = selectedJob.title;
        this.form.registration_requirement_status = null;
      } else {
        this.form.job_title = '';
        this.form.registration_requirement_status = 'NO_REGISTRATION_REQUIREMENT';
      }
    },
    update(item) {
      this.$validator.validateAll().then((result) => {
        if (result) {
          this.loadingButton = true;
          const payload = {
            companyId: this.selectedCompanyId,
            id: item.id,
            values: this.form,
          };
          this.updateApplication(payload)
            .then(() => {
              this.editApplicationDialog = false;
              this.loadingButton = false;
            })
            .catch((err) => {
              this.loadingButton = false;
            });
        }
      });
    },
  },
};

</script>
